import config from "../../config/config.json";

export function getRuntimeConfig() {
    const w = typeof window !== "undefined" ? window : null;
    const runtime =
        w && w.SitePilotChatWidgetConfig ? w.SitePilotChatWidgetConfig : {};
    return { ...config, ...runtime };
}

export const getToken = () => localStorage.getItem("token") || "";
export const getUserId = (u) => u?.id || u?._id || u?.userId || "";

export const convIdOf = (a, b) => {
    if (!a || !b) return "";
    return a < b ? `c_${a}_${b}` : `c_${b}_${a}`;
};

export const roomIdOf = (propertyId, visitorId, sessionId) => {
    const p = String(propertyId || "").trim();
    const v = String(visitorId || "").trim();
    const s = String(sessionId || "").trim();
    if (!p || !v || !s) return "";
    return `room_${p}_${v}_${s}`;
};

async function safeJson(res) {
    try {
        return await res.json();
    } catch {
        return {};
    }
}

export async function getMessages(convId) {
    if (!convId) return { messages: [] };
    const cfg = getRuntimeConfig();

    const res = await fetch(`${cfg.CHAT_HTTP_BASE}/messages/${convId}`, {
        method: "GET",
    });
    const data = await safeJson(res);
    if (!res.ok) throw new Error(data?.message || "Failed to load messages");
    return data;
}

function defaultMeta(meta = {}) {
    const safeWindow = typeof window !== "undefined" ? window : null;
    const safeDoc = typeof document !== "undefined" ? document : null;

    return {
        userType: meta.userType ?? "",
        pageName: meta.pageName ?? (safeDoc ? safeDoc.title : ""),
        pageURL: meta.pageURL ?? (safeWindow ? safeWindow.location.href : ""),
        type: meta.type ?? "message",
        userId: meta.userId ?? "",
        userName: meta.userName ?? "",
        userIcon: meta.userIcon ?? "",
    };
}

function roomIdentityPayload({ propertyId, visitorId, sessionId }) {
    const pid = String(propertyId || "").trim();
    const vid = String(visitorId || "").trim();
    const sid = String(sessionId || "").trim();
    return { propertyId: pid, visitorId: vid, sessionId: sid };
}

export class ChatSocket {
    constructor({
        userId,
        name,
        token,
        onOpen,
        onClose,
        onError,
        onEvent,
        autoReconnect = true,
        reconnectDelayMs = 1200,
        maxReconnectDelayMs = 12000,
    }) {
        this.userId = userId || "";
        this.name = name || "";
        this.token = token || "";
        this.onOpen = onOpen;
        this.onClose = onClose;
        this.onError = onError;
        this.onEvent = onEvent;

        this.autoReconnect = autoReconnect;
        this.reconnectDelayMs = reconnectDelayMs;
        this.maxReconnectDelayMs = maxReconnectDelayMs;

        this.ws = null;
        this.closedByUser = false;
        this._reconnectTimer = null;
        this._attempt = 0;
    }

    _buildUrl() {
        const cfg = getRuntimeConfig();

        if (this.userId) {
            const q = new URLSearchParams({
                userId: this.userId,
                name: this.name || this.userId,
            });
            return `${cfg.CHAT_WS_BASE}?${q.toString()}`;
        }

        if (this.token) {
            const q = new URLSearchParams({ token: this.token });
            return `${cfg.CHAT_WS_BASE}?${q.toString()}`;
        }

        throw new Error("ChatSocket: userId (preferred) or token is required");
    }

    connect() {
        if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) {
            return;
        }

        this.closedByUser = false;
        const ws = new WebSocket(this._buildUrl());
        this.ws = ws;

        ws.onopen = () => {
            this._attempt = 0;
            this.onOpen?.();
        };

        ws.onmessage = (evt) => {
            try {
                const env = JSON.parse(evt.data);
                if (env?.type && this.onEvent) {
                    this.onEvent(env.type, env.data);
                }
            } catch { }
        };

        ws.onerror = (e) => this.onError?.(e);

        ws.onclose = () => {
            this.onClose?.();

            if (this.autoReconnect && !this.closedByUser) {
                clearTimeout(this._reconnectTimer);
                this._attempt += 1;

                const delay = Math.min(
                    this.reconnectDelayMs * Math.pow(1.6, this._attempt),
                    this.maxReconnectDelayMs
                );

                this._reconnectTimer = setTimeout(() => this.connect(), delay);
            }
        };
    }

    disconnect() {
        this.closedByUser = true;
        clearTimeout(this._reconnectTimer);
        try {
            this.ws?.close();
        } catch { }
        this.ws = null;
    }

    isReady() {
        return this.ws && this.ws.readyState === 1;
    }

    send(type, data) {
        if (!this.isReady()) return false;
        this.ws.send(JSON.stringify({ type, data }));
        return true;
    }

    joinRoom({ propertyId, visitorId, sessionId }) {
        return this.send(
            "room:join",
            roomIdentityPayload({ propertyId, visitorId, sessionId })
        );
    }

    sendRoomMessage({
        propertyId,
        visitorId,
        sessionId,
        text,
        fileId,
        fileName,
        fileSize,
        mime,
        userType,
        pageName,
        pageURL,
        type,
        userId,
        userIcon,
        userName,
    }) {
        const meta = defaultMeta({
            userType,
            pageName,
            pageURL,
            type,
            userId,
            userIcon,
            userName,
        });

        return this.send("room:send", {
            ...roomIdentityPayload({ propertyId, visitorId, sessionId }),
            text,
            fileUrl: fileId,
            fileName,
            fileSize,
            mime,
            userType: meta.userType,
            pageName: meta.pageName,
            pageURL: meta.pageURL,
            type: meta.type,
            userId: meta.userId,
            userIcon: meta.userIcon,
            userName: meta.userName,
        });
    }

    sendRoomAlert({ uniqueId, message }) {
        const uid = String(uniqueId || "").trim();
        const msg = String(message || "").trim();
        if (!uid || !msg) return false;
        return this.send("room:alert", { uniqueId: uid, message: msg });
    }

    sendRoomAlertReceived({ uniqueId }) {
        const uid = String(uniqueId || "").trim();
        if (!uid) return false;
        return this.send("room:alertreceived", { uniqueId: uid });
    }

    sendRoomTyping({
        propertyId,
        visitorId,
        sessionId,
        text,
        type,
        userType,
        userIcon,
        userId,
        userName,
    }) {
        const meta = defaultMeta({ type, userType, userIcon, userId, userName });

        return this.send("room:typing", {
            ...roomIdentityPayload({ propertyId, visitorId, sessionId }),
            text: text ?? "",
            type: meta.type,
            userType: meta.userType,
            userId: meta.userId,
            userIcon: meta.userIcon,
            userName: meta.userName,
        });
    }

    visitorOpened({
        fingerprint,
        pageId,
        propertyId,
        visitorId,
        sessionId,
        ts,
        userType,
        userId,
        userIcon,
        userName,
    }) {
        const meta = defaultMeta({
            userType,
            userId,
            userIcon,
            userName,
            type: "visitor:opened",
        });

        return this.send("visitor:opened", {
            fingerprint,
            pageId,
            ...roomIdentityPayload({ propertyId, visitorId, sessionId }),
            ts,
            userType: meta.userType,
            userId: meta.userId,
            userIcon: meta.userIcon,
            userName: meta.userName,
        });
    }

    sendMessage({
        convId,
        to,
        text,
        fileId,
        fileName,
        fileSize,
        mime,
        propertyId,
        visitorId,
        sessionId,
        userType,
        pageName,
        pageURL,
        type,
        userId,
        userIcon,
        userName,
    }) {
        const meta = defaultMeta({
            userType,
            pageName,
            pageURL,
            type,
            userId,
            userIcon,
            userName,
        });

        return this.send("message:send", {
            convId,
            to,
            text,
            fileUrl: fileId,
            fileName,
            fileSize,
            mime,
            ...(propertyId || visitorId || sessionId
                ? roomIdentityPayload({ propertyId, visitorId, sessionId })
                : {}),
            userType: meta.userType,
            pageName: meta.pageName,
            pageURL: meta.pageURL,
            type: meta.type,
            userId: meta.userId,
            userIcon: meta.userIcon,
            userName: meta.userName,
        });
    }

    joinGroup({ convId, groupId, members }) {
        return this.send("group:join", { convId, groupId, members });
    }

    sendGroupMessage({
        convId,
        groupId,
        text,
        fileId,
        fileName,
        fileSize,
        mime,
        userType,
        pageName,
        pageURL,
        type,
        userId,
        userIcon,
        userName,
    }) {
        const meta = defaultMeta({
            userType,
            pageName,
            pageURL,
            type,
            userId,
            userIcon,
            userName,
        });

        return this.send("group:send", {
            convId,
            groupId,
            text,
            fileUrl: fileId,
            fileName,
            fileSize,
            mime,
            userType: meta.userType,
            pageName: meta.pageName,
            pageURL: meta.pageURL,
            type: meta.type,
            userId: meta.userId,
            userIcon: meta.userIcon,
            userName: meta.userName,
        });
    }

    webrtcSignal({
        convId,
        to,
        kind,
        sdp,
        candidate,
        fileId,
        fileName,
        fileSize,
        mime,
        callType,
    }) {
        return this.send("webrtc:signal", {
            convId,
            to,
            kind,
            sdp,
            candidate,
            fileId,
            fileUrl: fileId,
            fileName,
            fileSize,
            mime,
            callType,
        });
    }

    inviteAudioCall({
        convId,
        to,
        propertyId,
        visitorId,
        sessionId,
        userType,
        userId,
        userIcon,
        userName,
        pageName,
        pageURL,
    }) {
        const meta = defaultMeta({
            userType,
            userId,
            userIcon,
            userName,
            pageName,
            pageURL,
        });

        return this.send("call:invite", {
            convId,
            to,
            callType: "audio",
            ...(propertyId || visitorId || sessionId
                ? roomIdentityPayload({ propertyId, visitorId, sessionId })
                : {}),
            userType: meta.userType,
            userId: meta.userId,
            userIcon: meta.userIcon,
            userName: meta.userName,
            pageName: meta.pageName,
            pageURL: meta.pageURL,
            ts: Date.now(),
        });
    }

    acceptAudioCall({ callId, convId, to }) {
        return this.send("call:accept", {
            callId,
            convId,
            to,
            ts: Date.now(),
        });
    }

    rejectAudioCall({ callId, convId, to, reason = "rejected" }) {
        return this.send("call:reject", {
            callId,
            convId,
            to,
            reason,
            ts: Date.now(),
        });
    }

    endAudioCall({ callId, convId, to }) {
        return this.send("call:end", {
            callId,
            convId,
            to,
            ts: Date.now(),
        });
    }

    inviteVideoCall({
        convId,
        to,
        propertyId,
        visitorId,
        sessionId,
        userType,
        userId,
        userIcon,
        userName,
        pageName,
        pageURL,
    }) {
        const meta = defaultMeta({
            userType,
            userId,
            userIcon,
            userName,
            pageName,
            pageURL,
        });

        return this.send("call:invite", {
            convId,
            to,
            callType: "video",
            ...(propertyId || visitorId || sessionId
                ? roomIdentityPayload({ propertyId, visitorId, sessionId })
                : {}),
            userType: meta.userType,
            userId: meta.userId,
            userIcon: meta.userIcon,
            userName: meta.userName,
            pageName: meta.pageName,
            pageURL: meta.pageURL,
            ts: Date.now(),
        });
    }

    acceptVideoCall({ callId, convId, to }) {
        return this.send("call:accept", {
            callId,
            convId,
            to,
            ts: Date.now(),
        });
    }

    rejectVideoCall({ callId, convId, to, reason = "rejected" }) {
        return this.send("call:reject", {
            callId,
            convId,
            to,
            reason,
            ts: Date.now(),
        });
    }

    endVideoCall({ callId, convId, to }) {
        return this.send("call:end", {
            callId,
            convId,
            to,
            ts: Date.now(),
        });
    }

    typingStart(convId) {
        return this.send("typing:start", { convId });
    }

    typingStop(convId) {
        return this.send("typing:stop", { convId });
    }

    markSeen(convId, messageId) {
        return this.send("message:seen", { convId, messageId });
    }

    groupSeen(convId, messageId) {
        return this.send("group:seen", { convId, messageId });
    }
}